import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/utils'
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react'

interface SearchFilterProps {
  categories: any[]
  developers: any[]
  filters: any
  onFilterChange: (filters: Partial<any>) => void
}

export function SearchFilter({ categories, developers, filters, onFilterChange }: SearchFilterProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [priceRange, setPriceRange] = useState({
    min: filters.minPrice || 0,
    max: filters.maxPrice || 10000,
  })

  const handlePriceRangeChange = (type: 'min' | 'max', value: number) => {
    const newRange = { ...priceRange, [type]: value }
    setPriceRange(newRange)
    onFilterChange({ minPrice: newRange.min, maxPrice: newRange.max })
  }

  const handleReset = () => {
    onFilterChange({
      category: '',
      developer: '',
      minPrice: 0,
      maxPrice: 10000,
      rating: 0,
      featured: false,
    })
    setPriceRange({ min: 0, max: 10000 })
  }

  const activeFiltersCount = [
    filters.category,
    filters.developer,
    filters.rating > 0,
    filters.featured,
  ].filter(Boolean).length

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4" />
          <h3 className="font-semibold">Filters</h3>
          {activeFiltersCount > 0 && (
            <span className="badge badge-primary text-xs">{activeFiltersCount}</span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="text-muted-foreground hover:text-foreground"
          >
            Reset
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-6">
          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => onFilterChange({ category: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Developer Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Developer
            </label>
            <select
              value={filters.developer}
              onChange={(e) => onFilterChange({ developer: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Developers</option>
              {developers.map((developer) => (
                <option key={developer.id} value={developer.id}>
                  {developer.name}
                </option>
              ))}
            </select>
          </div>

          {/* Price Range Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Price Range
            </label>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Min: {formatCurrency(priceRange.min)}</label>
                <input
                  type="range"
                  min="0"
                  max="10000"
                  step="100"
                  value={priceRange.min}
                  onChange={(e) => handlePriceRangeChange('min', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Max: {formatCurrency(priceRange.max)}</label>
                <input
                  type="range"
                  min="0"
                  max="10000"
                  step="100"
                  value={priceRange.max}
                  onChange={(e) => handlePriceRangeChange('max', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Rating Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Minimum Rating
            </label>
            <div className="space-y-2">
              {[4, 3, 2, 1].map((rating) => (
                <label key={rating} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="rating"
                    checked={filters.rating === rating}
                    onChange={() => onFilterChange({ rating })}
                    className="text-primary focus:ring-primary"
                  />
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <span
                        key={i}
                        className={`text-sm ${i < rating ? 'text-yellow-400' : 'text-muted-foreground'}`}
                      >
                        ★
                      </span>
                    ))}
                    <span className="text-sm text-muted-foreground ml-1">& up</span>
                  </div>
                </label>
              ))}
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="rating"
                  checked={filters.rating === 0}
                  onChange={() => onFilterChange({ rating: 0 })}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-sm text-muted-foreground">All ratings</span>
              </label>
            </div>
          </div>

          {/* Featured Filter */}
          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.featured}
                onChange={(e) => onFilterChange({ featured: e.target.checked })}
                className="text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium text-foreground">Featured only</span>
            </label>
          </div>

          {/* Additional Filters */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Trading Style
            </label>
            <div className="space-y-2">
              {['Scalping', 'Day Trading', 'Swing Trading', 'Position Trading'].map((style) => (
                <label key={style} className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" className="text-primary focus:ring-primary" />
                  <span className="text-sm text-muted-foreground">{style}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Asset Class
            </label>
            <div className="space-y-2">
              {['Forex', 'Stocks', 'Crypto', 'Commodities', 'Indices'].map((asset) => (
                <label key={asset} className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" className="text-primary focus:ring-primary" />
                  <span className="text-sm text-muted-foreground">{asset}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}